<template>
  <div class="team container">
	    <ul v-if="team?.subNavList?.subNavigationReference?.length" class="generic__subnav">
	      <li v-for="link in team.subNavList.subNavigationReference" class="subnav__link">
        <nuxt-link v-if="link != null"
									:to="localePath(`/${link.slug.current}`)"
									class="generic__link">
					{{locale === 'en' ? link.text.en : link.text.jp}}
				</nuxt-link>
      </li>
    </ul>
    <h1 class="founder__heading">{{ mainHeader }}</h1>
	    <nuxt-link v-if="team?.founder?.slug?.current" :to="localePath(`/team/${team.founder.slug.current}`)" class="founder team__link">
      <figure class="team__figure row">
        <div class="col-xs-12 col-md-4">
          <img :src="$urlFor(team.founder.memberImage.asset.url, 1000)"
                   :alt="founderPortraitAlt"
                   class="team__image"
                   loading="lazy"
                   :height="team.founder.memberImage.asset.metadata.dimensions.height"
                   :width="team.founder.memberImage.asset.metadata.dimensions.width" />
        </div>
        <figcaption class="team__caption col-xs-12 col-md-8">
          <h4 class="team__name">{{founderName}}</h4>
          <h5 class="team__title">{{founderTitle}}</h5>
          <sanityContent :blocks="founderSubheader" />
        </figcaption>
      </figure>
    </nuxt-link>

    <h2 class="team__heading group__heading">{{ teamSubtitle }}</h2>
    <div class="team__group">
	      <nuxt-link v-for="member in (team.teamMembers || [])"
									 :to="localePath(`/team/${member.slug.current}`)"
									 class="team__link">
        <figure class="team__figure">
          <img :src="$urlFor(member.memberImage.asset.url, 700)"
                   :alt="locale === 'en' ? member.memberImageAlt.en : member.memberImageAlt.jp"
                   class="team__image"
                   :height="member.memberImage.asset.metadata.dimensions.height"
                   :width="member.memberImage.asset.metadata.dimensions.width" />
          <figcaption class="team__caption">
            <h4 class="team__name">{{ locale === 'en' ? member.name.en : member.name.jp }}</h4>
            <h5 class="team__title">{{ locale === 'en' ? member.jobTitle.en : member.jobTitle.jp }}</h5>
          </figcaption>
        </figure>
      </nuxt-link>
    </div>
  </div>
</template>

<script setup>
import { groq } from "@nuxtjs/sanity/runtime/groq";
import {useHead} from "nuxt/app";
import { useI18n, useLocalePath } from '#imports'
import {computed} from "vue";
const { locale } = useI18n()
const localePath = useLocalePath()
const query = groq`{
  'team': *[_type == 'team']{
    title,
    metaTitle,
    metaDescription,
    subNavList->{
      subNavigationReference[]->{
        slug,
        href,
        text
      }
    },
    founder->{
      name,
      slug,
      jobTitle,
      status,
      memberImage{
        asset->{
          metadata{
            dimensions{
              height,
              width
            }
          },
          url
          }
        },
      memberImageAlt,
      memberSubheader
    },
    subtitle,
    teamMembers[]->{
      name,
      slug,
      jobTitle,
      status,
      memberImage{
        asset->{
          metadata{
            dimensions{
              height,
              width
            }
          },
          url
        } },
      memberImageAlt,
      memberSubheader
    }
  }
}`;
const sanity = useSanity()
const { $getSafeLocalizedField } = useNuxtApp()
const { data } = await useAsyncData('page:team-index', () => sanity.fetch(query))
const team = computed(() => data.value?.team?.[0] ?? {})
let mainHeader = computed(() => locale.value === 'en' ? team.value?.title?.en : team.value?.title?.jp)
let founderName = computed(() => locale.value === 'en' ? team.value?.founder?.name?.en : team.value?.founder?.name?.jp)
let founderTitle = computed(() => locale.value === 'en' ? team.value?.founder?.jobTitle?.en : team.value?.founder?.jobTitle?.jp)
let founderPortraitAlt = computed(() => locale.value === 'en' ? team.value?.founder?.memberImageAlt?.en : team.value?.founder?.memberImageAlt?.jp)
let founderSubheader = computed(() => locale.value === 'en' ? team.value?.founder?.memberSubheader?.en : team.value?.founder?.memberSubheader?.jp)
let teamSubtitle = computed(() => locale.value === 'en' ? team.value?.subtitle?.en : team.value?.subtitle?.jp)
const htmlLang = computed(() => locale.value === 'en' ? 'en-US' : 'ja-JA')
const processedTitle = computed(() => {
	if (team.value.metaTitle) {
		return locale.value === 'en' ? team.value.metaTitle.en : team.value.metaTitle.jp
	}
	return `TokyoDex - ${teamSubtitle.value || ''}`
})
const fallbackDescription = 'Since 2012, TokyoDex has been a leader in transforming spaces through innovative art solutions.'
const processedDescription = computed(() => {
	const rawDescription = team.value?.metaDescription
	return $getSafeLocalizedField(rawDescription, locale.value, fallbackDescription)
})
// if(team.metaDescription != null) {
// 	processedDescription = computed(() => locale.value === 'en' ? team.metaDescription.en : team.metaDescription.jp )
// } else {
// 	processedDescription =  'TokyoDex - ' + teamSubtitle.value
// }
useHead({
	htmlAttrs: {
		lang: htmlLang
	},
	title: processedTitle,
	viewport: 'width=device-width, initial-scale=1, maximum-scale=1',
	charset: 'utf-8',
	meta: [
		{ name: 'description', content: processedDescription.value }
	],
})
</script>

<style lang="scss">
@use '@/assets/sass/variables.scss' as v;
.generic__subnav {
  margin: 6rem auto 2rem auto;
  padding: 0;
  position: relative;
	@include v.respond-to('small') {
		margin: 7rem auto 2rem auto;
	}
  li {
    display: inline-block;
    margin: 0 0.5rem 0 0;
    padding: 0;
		@include v.respond-to('small') {
			margin: 0 1rem 0 0;
		}
    a {
      @include v.button;
			margin: 0 0 0.5rem 0;
			padding: 0.5rem 0.75rem;
			@include v.respond-to('small') {
				margin: 1rem 0 0 0;
				padding: 0.5rem 1rem;
			}
			&.router-link-active {
				background-color: v.$blueGrey;
				color: v.$canvas;
				cursor: text;
				opacity: 0.75;
			}
    }
  }
}
.team {
  box-sizing: border-box;
  width: 100%;
  .founder__heading {
    font-size: 4rem;
		line-height: 4rem;
    margin: 3rem 0;
  }
  .founder {
    &.team__link {
			cursor: pointer;
      display: block;
    }
    .team__figure {
      display: flex;
      height: 100%;
      margin: 0;
    }
    .team__image {
      height: 100%;
    }
    .team__caption {
      height: auto;
      padding: 0 0.5rem;
			@include v.respond-to('small') {
				padding: 0 2rem;
			}
    }
    .team__name {
			font-family: var(--font);
      font-size: v.$font-size-lg;
      margin: 1rem 0;
			@include v.respond-to('small') {
				margin: 2rem 0 1rem 0;
			}
    }
  }
  &__heading {
    margin: 3rem 0;
  }
  &__group {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    grid-gap: 1rem;
    margin: 3rem 0;
    @include v.respond-to('small') {
      grid-template-columns: repeat(3, 1fr);
			grid-gap: 3rem;
    }
    @include v.respond-to('large') {
      grid-template-columns: repeat(4, 1fr);
    }
  }
  &__link {
		cursor: pointer;
    width: 100%;
    transition: 0.3s;
  }
  &__figure {
    height: 100%;
    width: 100%;
    margin: 0;
  }
  &__image {
    display: block;
    height: 18rem;
    object-fit: cover;
		object-position: top;
    width: 100%;
		@include v.respond-to('xlarge') {
			height: 30rem;
			object-position: center 5%;
		}
  }
  &__caption {
    height: 8rem;
    margin: 0;
    padding: 0 0.5rem;
    @include v.respond-to('small') {
      height: 6rem;
    }
  }
  &__name {
		font-family: var(--font);
    margin: 0;
    padding: 0.5rem 0 0 0;
  }
  &__title {
    margin: 0;
    padding: 0;
  }
}
</style>
